/**
 * MeReminderAlarm — customer-app alarm engine.
 *
 * Global component (mounted in MeLayout). Every 30s it evaluates active
 * pill reminders against `times_of_day` and fires an in-app alarm within
 * a ±60s window per HH:MM · date bucket (dedupe via localStorage).
 *
 * On fire:
 *   • Plays the tenant-published sound resolved by `/me/alarm-sound-for/medicine`
 *     (customer preference → tenant default → any active sound).
 *   • Shows a fullscreen modal with medicine name, loved-one, Snooze / Taken.
 *   • Also fires a browser Notification if permission is granted.
 *
 * NOTE: Ring while app closed → Phase 2 (Web Push + Service Worker).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { custApi, CUST_API_BASE } from "../../lib/customerApi";
import { RIGO_AVATAR_URL } from "../../components/rigo";
import { BellRinging, ClockCounterClockwise, CheckCircle } from "@phosphor-icons/react";

const SNOOZE_MIN = 10;
const CHECK_EVERY_MS = 30_000;
const REFRESH_EVERY_MS = 5 * 60_000;   // reload reminders every 5 min
const FIRE_WINDOW_SEC = 300;            // fire within ±5 min of the slot

const LABEL_TIME = {
  morning: { h: 8,  m: 0 },
  noon:    { h: 12, m: 30 },
  afternoon: { h: 13, m: 0 },
  evening: { h: 19, m: 0 },
  night:   { h: 22, m: 0 },
  bedtime: { h: 22, m: 30 },
};

const parseHM = (s) => {
  if (!s) return null;
  const t = String(s).trim().toLowerCase();
  if (LABEL_TIME[t]) return LABEL_TIME[t];
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return { h: Number(m[1]), m: Number(m[2]) };
};

const nowInWindow = (hm) => {
  const now = new Date();
  const target = new Date();
  target.setHours(hm.h, hm.m, 0, 0);
  return Math.abs((now - target) / 1000) <= FIRE_WINDOW_SEC;
};

const dedupeKey = (id, hm) => {
  const now = new Date();
  const d = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const t = `${String(hm.h).padStart(2, "0")}${String(hm.m).padStart(2, "0")}`;
  return `alarm:${id}:${d}:${t}`;
};

// Ask browser notification permission the first time a customer session opens.
async function ensureNotifPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission === "default") {
    try { await Notification.requestPermission(); } catch { /* ignore */ }
  }
}

// Web Speech API — Rigo "speaks" the reminder aloud
function speak(text, langHint) {
  try {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    // Cancel any prior utterance so we don't queue up if the user snoozes fast
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 1.05;
    u.volume = 1;
    if (langHint) u.lang = langHint;
    // Prefer a matching-language voice if available
    const voices = window.speechSynthesis.getVoices();
    if (voices?.length) {
      const match = voices.find(v => v.lang?.toLowerCase().startsWith((langHint || "en").slice(0, 2)))
                     || voices.find(v => v.default);
      if (match) u.voice = match;
    }
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

async function fetchLovedOnes() {
  try {
    const r = await custApi.get("/me/loved-ones");
    const rows = r.data?.loved_ones || r.data || [];
    return Object.fromEntries(rows.map(l => [l.id, l]));
  } catch { return {}; }
}

async function fetchReminders() {
  try {
    const r = await custApi.get("/me/pill-reminders");
    return (r.data?.reminders || []).filter(x => x.active);
  } catch { return []; }
}

async function resolveSoundUrl(lovedOneId) {
  try {
    const suffix = lovedOneId ? `?loved_one_id=${encodeURIComponent(lovedOneId)}` : "";
    const r = await custApi.get(`/me/alarm-sound-for/medicine${suffix}`);
    const s = r.data?.sound;
    if (!s?.url) return null;
    return s.url.startsWith("/")
      ? `${CUST_API_BASE.replace(/\/api$/, "")}${s.url}`
      : s.url;
  } catch { return null; }
}

export default function MeReminderAlarm() {
  const [active, setActive] = useState(null);   // { reminder, loved_one, slot }
  const reminders = useRef([]);
  const lovedOnes = useRef({});
  const audioRef = useRef(null);
  const snoozedUntil = useRef({});               // key → epoch ms

  const load = useCallback(async () => {
    reminders.current = await fetchReminders();
    lovedOnes.current = await fetchLovedOnes();
  }, []);

  useEffect(() => {
    ensureNotifPermission();
    load();
    const refresh = setInterval(load, REFRESH_EVERY_MS);
    return () => clearInterval(refresh);
  }, [load]);

  const fire = useCallback(async (reminder, slot) => {
    const key = dedupeKey(reminder.id, slot);
    const snoozeUntil = snoozedUntil.current[key];
    if (snoozeUntil && Date.now() < snoozeUntil) return;
    if (localStorage.getItem(key) === "done") return;

    setActive({
      reminder,
      loved_one: lovedOnes.current[reminder.loved_one_id] || null,
      slot,
      key,
    });

    // Resolve sound per-loved-one every fire (so per-profile choices apply)
    const url = await resolveSoundUrl(reminder.loved_one_id);
    if (url) {
      try {
        const audio = new Audio(url);
        audio.loop = false;
        let plays = 0;
        audio.addEventListener("ended", () => {
          plays += 1;
          if (plays < 3) audio.play().catch(() => {});
        });
        audio.play().catch(() => {});
        audioRef.current = audio;
      } catch { /* noop */ }
    }

    // Voice announcement — Rigo speaks the reminder aloud (English default)
    const person = (lovedOnes.current[reminder.loved_one_id]?.name || "").trim();
    const med = reminder.medicine_name || "medicine";
    const dose = reminder.dosage ? `, ${reminder.dosage}` : "";
    const phrase = person
      ? `${person}, it's time for your ${med}${dose}.`
      : `It's time for your ${med}${dose}.`;
    // Slight delay so it doesn't clash with the alarm sound's first note
    setTimeout(() => speak(phrase, "en-IN"), 800);

    // Browser notification (mostly for tabs in background)
    try {
      if (Notification.permission === "granted") {
        const n = new Notification("Medicine time", {
          body: `${reminder.medicine_name}${reminder.dosage ? ` · ${reminder.dosage}` : ""}`,
          icon: "/brand/rescigo-mark-128.png",
          tag: `pill-${reminder.id}-${slot.h}-${slot.m}`,
          renotify: true,
        });
        n.onclick = () => { window.focus(); n.close(); };
      }
    } catch { /* noop */ }
  }, []);

  // Ticker
  useEffect(() => {
    const tick = () => {
      if (active) return;   // already firing
      const now = new Date();
      // Use LOCAL date so start_date/end_date match the user's calendar,
      // not UTC (matters near midnight IST vs UTC).
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      for (const r of reminders.current) {
        if (r.start_date && today < r.start_date) continue;
        if (r.end_date && today > r.end_date) continue;
        for (const t of (r.times_of_day || [])) {
          const hm = parseHM(t);
          if (!hm) continue;
          if (!nowInWindow(hm)) continue;
          fire(r, hm);
          return;
        }
      }
    };
    tick(); // run once immediately
    const iv = setInterval(tick, CHECK_EVERY_MS);
    return () => clearInterval(iv);
  }, [active, fire]);

  // Manual test trigger — dispatched from a "Test alarm" button in the app
  useEffect(() => {
    const onTest = (e) => {
      const detail = e?.detail || {};
      fire({
        id: "__test__",
        medicine_name: detail.medicine_name || "Metformin 500mg",
        dosage: detail.dosage || "1 tablet after food",
        loved_one_id: detail.loved_one_id || null,
      }, { h: new Date().getHours(), m: new Date().getMinutes() });
    };
    window.addEventListener("rigo:test-alarm", onTest);
    return () => window.removeEventListener("rigo:test-alarm", onTest);
  }, [fire]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    // Also stop any voice announcement in progress
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
  };

  // Listen for OS notification action buttons (Take / Snooze) — the service
  // worker relays them via postMessage so the in-app popup dismisses too.
  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type !== "ALARM_ACTION") return;
      if (d.action === "taken") markDone();
      else if (d.action === "snooze") snooze();
    };
    navigator.serviceWorker?.addEventListener("message", onMsg);
    return () => navigator.serviceWorker?.removeEventListener("message", onMsg);
  }, [active]);

  const markDone = () => {
    if (active) localStorage.setItem(active.key, "done");
    stopAudio();
    setActive(null);
  };
  const snooze = () => {
    if (active) snoozedUntil.current[active.key] = Date.now() + SNOOZE_MIN * 60_000;
    stopAudio();
    setActive(null);
  };

  if (!active) return null;

  const lovedName = active.loved_one?.name || "you";
  const slotStr = `${String(active.slot.h).padStart(2,"0")}:${String(active.slot.m).padStart(2,"0")}`;

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn"
         data-testid="me-alarm-modal">
      {/* Floating rings */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-[#DC2626]/20 animate-alarm-ring"/>
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-[#DC2626]/20 animate-alarm-ring" style={{ animationDelay: "0.6s" }}/>
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-[#DC2626]/20 animate-alarm-ring" style={{ animationDelay: "1.2s" }}/>
      </div>

      <div className="relative w-full max-w-sm">
        {/* Rigo mascot bursting into frame */}
        <div className="relative flex flex-col items-center">
          <div className="relative animate-rigo-enter">
            <img
              src={RIGO_AVATAR_URL}
              alt="Rigo"
              className="w-56 h-56 object-contain drop-shadow-[0_20px_30px_rgba(220,38,38,0.55)] animate-rigo-bounce"
            />
            {/* Ringing bell floating next to Rigo's ear */}
            <div className="absolute top-8 -right-2 w-12 h-12 rounded-full bg-[#DC2626] text-white flex items-center justify-center shadow-lg animate-ring-shake">
              <BellRinging size={22} weight="fill"/>
            </div>
            {/* Sparkles */}
            <div className="absolute -top-2 left-6 text-[#F5C518] text-2xl animate-sparkle" style={{ animationDelay: "0.2s" }}>✦</div>
            <div className="absolute top-14 -left-4 text-[#F5C518] text-lg animate-sparkle" style={{ animationDelay: "0.6s" }}>✦</div>
            <div className="absolute bottom-8 -right-6 text-[#F5C518] text-xl animate-sparkle" style={{ animationDelay: "0.9s" }}>✦</div>
          </div>

          {/* Speech bubble (points to Rigo) */}
          <div className="relative -mt-4 w-full bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden animate-bubble-in">
            {/* Bubble arrow */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 bg-white border-l border-t border-gray-100 rotate-45"/>

            <div className="relative px-6 pt-5 pb-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#DC2626]">Rigo says</div>
              <div className="mt-2 font-display font-black text-[22px] leading-tight text-[#111827]">
                {lovedName === "you" ? "Time for your medicine!" : `${lovedName} — time for medicine!`}
              </div>
              <div className="mt-2 text-[13px] text-[#5C5A55]">
                <span className="font-bold text-[#DC2626]" data-testid="me-alarm-med">{active.reminder.medicine_name}</span>
                {active.reminder.dosage && <span> · {active.reminder.dosage}</span>}
              </div>
              <div className="mt-1.5 text-[11px] text-[#8A8780]">
                Scheduled for <span className="font-mono font-bold">{slotStr}</span>
              </div>
            </div>

            <div className="px-4 pb-4 space-y-2">
              <button onClick={markDone} data-testid="me-alarm-taken"
                className="w-full h-12 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] hover:brightness-110 text-white rounded-2xl font-bold text-sm inline-flex items-center justify-center gap-2 shadow-lg shadow-red-500/20 active:scale-[0.98] transition">
                <CheckCircle size={16} weight="fill"/> I&apos;ve taken it
              </button>
              <button onClick={snooze} data-testid="me-alarm-snooze"
                className="w-full h-12 bg-white border border-gray-200 text-[#111827] rounded-2xl font-bold text-sm inline-flex items-center justify-center gap-2 hover:bg-gray-50 active:scale-[0.98] transition">
                <ClockCounterClockwise size={16} weight="bold"/> Snooze {SNOOZE_MIN} min
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.25s ease-out; }

        @keyframes rigoEnter {
          0%   { transform: translateY(-40px) scale(0.6) rotate(-10deg); opacity: 0; }
          60%  { transform: translateY(6px)   scale(1.08) rotate(3deg);  opacity: 1; }
          100% { transform: translateY(0)     scale(1)    rotate(0);      opacity: 1; }
        }
        .animate-rigo-enter { animation: rigoEnter 0.55s cubic-bezier(0.22, 1.5, 0.36, 1); }

        @keyframes rigoBounce {
          0%, 100% { transform: translateY(0)   rotate(-1deg); }
          25%      { transform: translateY(-6px) rotate(2deg); }
          50%      { transform: translateY(0)   rotate(-1deg); }
          75%      { transform: translateY(-4px) rotate(1.5deg); }
        }
        .animate-rigo-bounce { animation: rigoBounce 2.4s ease-in-out infinite; }

        @keyframes bubbleIn {
          0%   { transform: scale(0.85) translateY(20px); opacity: 0; }
          70%  { transform: scale(1.03) translateY(0);    opacity: 1; }
          100% { transform: scale(1)    translateY(0);    opacity: 1; }
        }
        .animate-bubble-in { animation: bubbleIn 0.5s 0.25s both cubic-bezier(0.22, 1.5, 0.36, 1); }

        @keyframes ringShake {
          0%, 100% { transform: rotate(0); }
          15%      { transform: rotate(-22deg); }
          30%      { transform: rotate(20deg); }
          45%      { transform: rotate(-14deg); }
          60%      { transform: rotate(14deg); }
          75%      { transform: rotate(-6deg); }
        }
        .animate-ring-shake { animation: ringShake 0.9s ease-in-out infinite; }

        @keyframes sparkle {
          0%, 100% { transform: scale(0) rotate(0);    opacity: 0; }
          50%      { transform: scale(1.4) rotate(180deg); opacity: 1; }
        }
        .animate-sparkle { animation: sparkle 1.8s ease-in-out infinite; }

        @keyframes alarmRing {
          0%   { transform: translate(-50%, 0) scale(0.4); opacity: 0.7; }
          100% { transform: translate(-50%, 0) scale(2.6); opacity: 0; }
        }
        .animate-alarm-ring { animation: alarmRing 2s ease-out infinite; }
      `}</style>
    </div>
  );
}
